import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Save, RefreshCw } from 'lucide-react';

export default function GoogleAdManagerTab({ config, setConfig, saving, onSave, onToggle }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Google Ad Manager Settings</span>
          <Badge variant={config.enabled ? 'default' : 'secondary'}>
            {config.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </CardTitle>
        <CardDescription>
          Configure your Google Ad Manager credentials to serve Google ads alongside partner ads
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div>
            <Label className="text-base font-medium">Enable Google Ads</Label>
            <p className="text-sm text-muted-foreground">
              When disabled, only partner/sponsor ads will be shown
            </p>
          </div>
          <Switch
            checked={config.enabled}
            onCheckedChange={onToggle}
            data-testid="gam-toggle"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="networkCode">Network Code</Label>
          <Input
            id="networkCode"
            placeholder="e.g., 12345678"
            value={config.network_code}
            onChange={(e) => setConfig({ ...config, network_code: e.target.value })}
            disabled={!config.enabled}
            data-testid="network-code-input"
          />
          <p className="text-xs text-muted-foreground">
            Find this in Google Ad Manager → Admin → Global Settings → Network Code
          </p>
        </div>

        <div className="space-y-4">
          <Label className="text-base">Ad Unit Paths</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Enter the full ad unit path for each placement (e.g., /12345678/homepage_banner)
          </p>

          <div className="grid gap-4">
            {Object.entries(config.ad_units || {}).map(([key, value]) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={key} className="text-sm capitalize">
                  {key.replace(/_/g, ' ')}
                </Label>
                <Input
                  id={key}
                  placeholder={`/${config.network_code || 'XXXXXXXX'}/${key}`}
                  value={value}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      ad_units: { ...config.ad_units, [key]: e.target.value },
                    })
                  }
                  disabled={!config.enabled}
                />
              </div>
            ))}
          </div>
        </div>

        <Button
          onClick={onSave}
          className="w-full"
          disabled={saving}
          data-testid="save-config-btn"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Configuration
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
